import { MenuList, Tooltip } from "@mui/material";
import { CoreMenuPopover } from "../menu";
import { useId, useRef } from "react";
import i18nPackage from "../../i18n/i18n";
import { CoreIconButton } from "./Button";
import { CoreProfileImage } from "../profile-image";
import styles from "./ProfileButton.module.css";
import UserMenuItem from "../menu/UserMenuItem";
import SignOutIcon from "../menu/images/signOut.svg?react";

type Role = "yujaAdmin" | "customerAdmin" | "accountManager";

type ProfileButtonProps = {
	children: React.ReactNode;
	name?: string;
	email?: string;
	menuShowing: boolean;
	onMenuToggle: (showing: boolean) => void;
	showLogout?: boolean;
	onLogout?: () => void;
	role?: Role;
};

export default function ProfileButton({
	children,
	name,
	email,
	menuShowing,
	onMenuToggle,
	showLogout = true,
	onLogout,
	role,
}: ProfileButtonProps) {
	const buttonRef = useRef<HTMLButtonElement | null>(null);
	const buttonId = useId();
	const menuId = useId();

	const roleText = role ? i18nPackage.t(`coreComponents.userMenu.${role}`) : undefined;

	return (
		<>
			<CoreIconButton
				id={buttonId}
				variant="plain"
				className={styles.profileMenuButton}
				onClick={() => onMenuToggle(true)}
				aria-haspopup="menu"
				aria-expanded={menuShowing}
				aria-controls={menuId}
				ref={buttonRef}
				tooltip={i18nPackage.t("coreComponents.profileButton.myAccount")}
			>
				<CoreProfileImage className={styles.profileIcon} name={name} alt={""} role="presentation" />
			</CoreIconButton>
			{menuShowing && (
				<CoreMenuPopover
					id={menuId}
					open
					onClose={() => onMenuToggle(false)}
					anchorEl={buttonRef.current}
					anchorOrigin={{
						vertical: "bottom",
						horizontal: "left",
					}}
				>
					<div role="group" className={styles.userMenu}>
						<div className={styles.menuInfoArea}>
							<CoreProfileImage className={styles.profileIcon} name={name} alt={""} role="presentation" size={44} />
							<div className={styles.menuInfoAreaDetails}>
								{name && (
									<Tooltip title={name} describeChild>
										<span className={styles.name}>{name}</span>
									</Tooltip>
								)}
								{roleText && <span className={styles.roleText}>{roleText}</span>}
								{email && (
									<Tooltip title={email} describeChild>
										<span className={styles.infoAreaText}>{email}</span>
									</Tooltip>
								)}
							</div>
						</div>
						<MenuList autoFocusItem={menuShowing} aria-labelledby={buttonId}>
							{children}
							{showLogout && onLogout && (
								<UserMenuItem
									data-testid="logout"
									onClick={onLogout}
									Icon={SignOutIcon}
									className={styles.logoutButton}
								>
									{i18nPackage.t("coreComponents.userMenu.signOut")}
								</UserMenuItem>
							)}
						</MenuList>
					</div>
				</CoreMenuPopover>
			)}
		</>
	);
}
